// Provides pure model/derivation helpers for sheet totals, labels, widgets, and entry-list rows.
import type { SheetAccountEntry } from "../../types/sheetDocument";
import type { EntryListTableRow } from "./components/EntryListTable";
import type { RelatedSubtotalRow } from "./types";

type TAccountEntry = SheetAccountEntry;

export function computeRelatedSubtotals(tAccounts: TAccountEntry[]): RelatedSubtotalRow[] {
  const subtotalMap = new Map<string, RelatedSubtotalRow>();

  tAccounts.forEach((account) => {
    account.entries.forEach((entry) => {
      if (entry.isBeginningBalance) {
        return;
      }

      const existing = subtotalMap.get(entry.letter) ?? {
        letter: entry.letter,
        debitSubtotal: 0,
        creditSubtotal: 0,
      };

      existing.debitSubtotal += entry.debitAmount ?? 0;
      existing.creditSubtotal += entry.creditAmount ?? 0;
      subtotalMap.set(entry.letter, existing);
    });
  });

  return Array.from(subtotalMap.values()).sort((left, right) => left.letter.localeCompare(right.letter));
}

export function computeImbalancedRelatedSubtotals(relatedSubtotals: RelatedSubtotalRow[]): RelatedSubtotalRow[] {
  return relatedSubtotals.filter((subtotal) => Math.abs(subtotal.debitSubtotal - subtotal.creditSubtotal) > 1e-9);
}

export function computeImbalancedLetters(imbalancedRelatedSubtotals: RelatedSubtotalRow[]): Set<string> {
  return new Set(imbalancedRelatedSubtotals.map((subtotal) => subtotal.letter));
}

export function computeDisplayLetterBySequence(tAccounts: TAccountEntry[]): Map<number, string> {
  const allEntries = tAccounts
    .flatMap((account) => account.entries)
    .slice()
    .sort((left, right) => left.sequence - right.sequence);

  const letterTotals = new Map<string, number>();
  allEntries.forEach((entry) => {
    letterTotals.set(entry.letter, (letterTotals.get(entry.letter) ?? 0) + 1);
  });

  const letterProgress = new Map<string, number>();
  const labels = new Map<number, string>();
  allEntries.forEach((entry) => {
    const currentIndex = (letterProgress.get(entry.letter) ?? 0) + 1;
    letterProgress.set(entry.letter, currentIndex);
    const totalForLetter = letterTotals.get(entry.letter) ?? 0;
    labels.set(entry.sequence, totalForLetter > 1 ? `${entry.letter}.${currentIndex}` : entry.letter);
  });

  return labels;
}

export function buildWidgetEntries(
  account: TAccountEntry,
  displayLetterBySequence: Map<number, string>,
  imbalancedLetters: Set<string>,
) {
  return account.entries
    .slice()
    .sort((left, right) => {
      if (left.isBeginningBalance && !right.isBeginningBalance) {
        return -1;
      }

      if (!left.isBeginningBalance && right.isBeginningBalance) {
        return 1;
      }

      return left.sequence - right.sequence;
    })
    .map((lineEntry) => ({
      ...lineEntry,
      displayLetter: lineEntry.isBeginningBalance
        ? "BBal"
        : (displayLetterBySequence.get(lineEntry.sequence) ?? lineEntry.letter),
      displayReference: lineEntry.isBeginningBalance
        ? "bbal"
        : (displayLetterBySequence.get(lineEntry.sequence) ?? lineEntry.letter).toLowerCase(),
      isUnbalanced: !lineEntry.isBeginningBalance && imbalancedLetters.has(lineEntry.letter),
    }));
}

export function buildEntryListRows(
  tAccounts: TAccountEntry[],
  displayLetterBySequence: Map<number, string>,
): EntryListTableRow[] {
  const parseDisplayLetter = (value: string): { base: string; index: number } => {
    const match = value.match(/^([a-zA-Z])(?:\.(\d+))?$/);
    if (!match) {
      return { base: value.toLowerCase(), index: 0 };
    }

    return {
      base: match[1].toLowerCase(),
      index: match[2] ? Number(match[2]) : 0,
    };
  };

  return tAccounts
    .flatMap((account) => account.entries.map((entry) => ({ account: account.account, entry })))
    .map(({ account, entry }) => ({
      sequence: entry.sequence,
      displayLetter: entry.isBeginningBalance ? "BBal" : (displayLetterBySequence.get(entry.sequence) ?? entry.letter),
      displayReference: entry.isBeginningBalance
        ? "bbal"
        : (displayLetterBySequence.get(entry.sequence) ?? entry.letter).toLowerCase(),
      letter: entry.letter,
      isBeginningBalance: !!entry.isBeginningBalance,
      account,
      debitAmount: entry.debitAmount,
      creditAmount: entry.creditAmount,
      isCredit: entry.creditAmount !== undefined,
      comment: entry.comment,
    }))
    .sort((left, right) => {
      if (left.isBeginningBalance && !right.isBeginningBalance) {
        return -1;
      }

      if (!left.isBeginningBalance && right.isBeginningBalance) {
        return 1;
      }

      const leftLabel = parseDisplayLetter(left.displayLetter);
      const rightLabel = parseDisplayLetter(right.displayLetter);
      const baseCompare = leftLabel.base.localeCompare(rightLabel.base);

      if (baseCompare !== 0) {
        return baseCompare;
      }

      if (leftLabel.index !== rightLabel.index) {
        return leftLabel.index - rightLabel.index;
      }

      return left.sequence - right.sequence;
    });
}
